/**
 * API Access Section — settings section for managing service accounts.
 *
 * Lists service-account credentials with new-token, edit and delete actions.
 * Clicking a row (or its edit icon) opens the CRM object slideout for that
 * credential. "New token" mints an additional bearer token for an existing
 * credential (see use-mint-token.ts for why the record editor cannot). An
 * "Add" button opens the create-credential slideout for minting a new
 * principal. Only members of ORG_REACH_ADMIN_GROUP can write; everyone else
 * sees the same list with write actions disabled rather than hidden.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from '@untitledui/icons';
import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { usePermissions } from '@/hooks/usePermissions';
import { ButtonList } from '@/lib/base/button-list';
import { Button } from '@/lib/base/buttons/button';
import { QueryErrorState } from '@/lib/components/QueryErrorState';
import { WebhookSecretCreatedModal } from '@/lib/crm/components/webhooks/WebhookSecretCreatedModal';
import { useOpenSlideout } from '@/lib/crm/hooks/useOpenSlideout';
import {
  crmObjectListSentinelQuery,
  useStoreBackedList,
} from '@/lib/crm/store';
import { useViewHealthMarker } from '@/lib/view-health';

import { ApiAccessCreateSlideout } from './api-access-create-slideout';
import { ApiAccessListItem } from './api-access-list-item';
import { ORG_REACH_ADMIN_GROUP } from './api-access-service';
import { TokenSelfCheckInstruction } from './api-access-token-instruction';
import { useMintToken } from './use-mint-token';

// ahoy_permissions is needed here (not just on the create form) so the list
// row's scope summary (describeScope) can render without a second fetch.
// ahoy_reach decides the "Whole org" badge; older service accounts have none,
// which reads as user reach (see credentialReachOf).
const SERVICE_ACCOUNT_PROPERTIES = [
  'ahoy_display_name',
  'ahoy_created_at',
  'ahoy_permissions',
  'ahoy_reach',
];

// ============================================================================
// ApiAccessContent
// ============================================================================

function ApiAccessContent() {
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id;
  const queryClient = useQueryClient();
  const openSlideout = useOpenSlideout();
  const { hasPermissionGroup } = usePermissions();
  const canWrite = hasPermissionGroup(ORG_REACH_ADMIN_GROUP);

  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const serviceAccountQuery = useMemo(
    () =>
      crmObjectListSentinelQuery(orgId ?? 0, 'ahoy_service_account', {
        properties: SERVICE_ACCOUNT_PROPERTIES,
        page_size: 20,
      }),
    [orgId]
  );

  const serviceAccountListQuery = useQuery({
    ...serviceAccountQuery,
    enabled: !!orgId,
  });

  const serviceAccounts = useStoreBackedList(serviceAccountListQuery);
  const {
    refetch: refetchServiceAccounts,
    isLoading,
    error: serviceAccountsError,
    results: serviceAccountResults,
  } = serviceAccounts;

  // Route health: the section counts as loaded once the list has settled.
  // Without this the /settings/api-access scope only ever times out.
  useViewHealthMarker('settings-section-ready', { ready: !isLoading });
  const fatalError = serviceAccountsError ?? null;

  const credentials = serviceAccountsError ? [] : serviceAccountResults;

  const handleInvalidate = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: serviceAccountQuery.queryKey,
      exact: false,
    });
  }, [queryClient, serviceAccountQuery.queryKey]);

  const handleOpenCreate = useCallback(() => {
    setIsCreateOpen(true);
  }, []);

  const handleRetry = useCallback(() => {
    refetchServiceAccounts();
  }, [refetchServiceAccounts]);

  // Owned here (always mounted) rather than in the row, so a mint survives the
  // list refetching mid-flight and one reveal modal serves every row.
  const { mintToken, mintingCredentialId, mintedToken, dismissMintedToken } =
    useMintToken(orgId ?? 0, handleInvalidate);

  if (!orgId) {
    return null;
  }

  const hasCredentials = credentials.length > 0;

  // The loading and error states swap only the LIST; the reveal modal and the
  // create slideout below stay mounted through them. A list refetch (every
  // mint invalidates both lists) can flip a query back to loading — e.g. one
  // that errored and holds no data — and an early return here would unmount
  // the create slideout mid-mint, dropping the one-time token before it is shown.
  let listContent: ReactNode;
  if (isLoading) {
    listContent = (
      <div
        role='status'
        aria-live='polite'
        aria-label='Loading service accounts'
        className='space-y-3'
      >
        {[1, 2].map(i => (
          <div key={i} className='h-12 bg-bg-tertiary rounded animate-pulse' />
        ))}
      </div>
    );
  } else if (fatalError) {
    listContent = (
      <QueryErrorState
        error={fatalError}
        label="We couldn't load your service accounts"
        onRetry={handleRetry}
      />
    );
  } else if (!hasCredentials) {
    listContent = (
      <div
        role='status'
        aria-live='polite'
        className='flex flex-col items-center justify-center py-12 text-center'
      >
        <p className='text-sm text-fg-tertiary'>No service accounts yet.</p>
        <p className='mt-1 mb-4 max-w-md text-sm text-fg-tertiary'>
          Each service account gets a token and reads only the data you grant
          it.
        </p>
        <Button
          size='sm'
          color='primary'
          iconLeading={Plus}
          onClick={handleOpenCreate}
          isDisabled={!canWrite}
          disabledTooltip='Only admins can create service accounts'
        >
          Add
        </Button>
      </div>
    );
  } else {
    listContent = (
      <div>
        <div className='flex justify-end mb-3'>
          <Button
            size='sm'
            color='secondary'
            iconLeading={Plus}
            onClick={handleOpenCreate}
            isDisabled={!canWrite}
            disabledTooltip='Only admins can create service accounts'
          >
            Add
          </Button>
        </div>
        <ButtonList aria-label='Service accounts'>
          {credentials.map(credential => (
            <ApiAccessListItem
              key={credential.entity_id}
              credential={credential}
              orgId={orgId}
              canWrite={canWrite}
              onRowClick={openSlideout}
              onDeleted={handleInvalidate}
              onMintToken={mintToken}
              isMintingToken={mintingCredentialId === credential.entity_id}
            />
          ))}
        </ButtonList>
      </div>
    );
  }

  return (
    <>
      {listContent}

      <WebhookSecretCreatedModal
        isOpen={!!mintedToken}
        onClose={dismissMintedToken}
        data={mintedToken?.data ?? null}
        instruction={<TokenSelfCheckInstruction />}
      />

      <ApiAccessCreateSlideout
        isOpen={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        orgId={orgId}
        onMinted={handleInvalidate}
      />
    </>
  );
}

// ============================================================================
// ApiAccessSection
// ============================================================================

export function ApiAccessSection() {
  return (
    <div>
      <h2 className='text-lg font-semibold text-fg-primary'>
        Service accounts
      </h2>
      <p className='text-sm text-fg-tertiary mt-0.5'>
        Service accounts let other tools read and write your Ahoy data.
      </p>
      <ApiAccessContent />
    </div>
  );
}
